<template>
  <a-modal
    :open="visible"
    title="목적지 URL 변경"
    :width="600"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="modal-content">
      <!-- 현재 QR코드 정보 -->
      <div class="qr-info-section">
        <div class="info-row">
          <span class="label">QR코드명:</span>
          <span class="value">{{ qrData?.name }}</span>
        </div>
        <div class="info-row">
          <span class="label">Short Code:</span>
          <code>{{ qrData?.shortCode }}</code>
        </div>
      </div>

      <a-divider />

      <!-- URL 변경 폼 -->
      <a-form layout="vertical">
        <a-form-item label="현재 URL">
          <a-input :value="qrData?.targetUrl" disabled />
        </a-form-item>

        <a-form-item label="새 URL" required>
          <a-input
            v-model:value="newUrl"
            placeholder="https://example.com/new-page"
            @blur="validateUrl"
          />
          <div class="input-hint error" v-if="!isValidUrl && newUrl">
            올바른 URL 형식을 입력해주세요
          </div>
          <div class="input-hint info" v-if="isValidUrl && hasKorean">
            <i class="fa-solid fa-circle-info"></i>
            한글이 포함된 URL은 자동으로 인코딩되어 등록됩니다.
          </div>
        </a-form-item>
      </a-form>

      <!-- URL 변경 이력 -->
      <div class="history-section" v-if="urlHistory.length > 0">
        <h4>URL 변경 이력</h4>
        <a-table
          :columns="historyColumns"
          :data-source="urlHistory"
          :pagination="false"
          size="small"
          :row-key="record => record._id"
        >
          <template #previousUrl="{ record }">
            <div class="url-cell">{{ record.previousUrl }}</div>
          </template>
          <template #action="{ record }">
            <a-button type="link" size="small" @click="restoreUrl(record)">
              복원
            </a-button>
          </template>
        </a-table>
      </div>

      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button type="primary" class="yellow-button" :loading="loading" @click="handleSubmit">
          URL 변경
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import QrService from '../service/QrService';
import dayjs from 'dayjs';

export default defineComponent({
  name: 'QrUrlChangeModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    qrData: {
      type: Object,
      default: null
    }
  },
  emits: ['update:visible', 'success'],
  setup(props, { emit }) {
    const loading = ref(false);
    const newUrl = ref('');
    const isValidUrl = ref(false);
    const hasKorean = ref(false);
    const urlHistory = ref([]);

    const historyColumns = [
      {
        title: '변경일시',
        dataIndex: 'changedAt',
        width: 160,
        customRender: ({ text }) => dayjs(text).format('YYYY-MM-DD HH:mm')
      },
      {
        title: '이전 URL',
        key: 'previousUrl',
        slots: { customRender: 'previousUrl' }
      },
      {
        title: '',
        key: 'action',
        width: 80,
        slots: { customRender: 'action' }
      }
    ];

    const validateUrl = () => {
      // 한글 포함 여부 체크
      const koreanPattern = /[가-힣]/;
      hasKorean.value = koreanPattern.test(newUrl.value);

      // URL 유효성 검사 (한글 및 유니코드 문자 허용)
      try {
        const url = newUrl.value.trim();
        if (!url) {
          isValidUrl.value = false;
          return;
        }

        // http/https가 없으면 https 붙여서 검사
        const urlToTest = url.match(/^https?:\/\//) ? url : `https://${url}`;
        new URL(urlToTest);
        isValidUrl.value = true;
      } catch {
        isValidUrl.value = false;
      }
    };

    const loadUrlHistory = async () => {
      if (!props.qrData?._id) return;

      try {
        const result = await QrService.getUrlHistory(props.qrData._id, { limit: 5 });
        urlHistory.value = result.items || [];
      } catch (error) {
        console.error('URL 이력 조회 오류:', error);
      }
    };

    watch(() => props.visible, (newVal) => {
      if (newVal && props.qrData) {
        newUrl.value = '';
        isValidUrl.value = false;
        loadUrlHistory();
      }
    });

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const handleSubmit = async () => {
      if (!newUrl.value?.trim()) {
        message.warning('새 URL을 입력해주세요.');
        return;
      }

      if (!isValidUrl.value) {
        message.warning('올바른 URL 형식을 입력해주세요.');
        return;
      }

      if (newUrl.value === props.qrData?.targetUrl) {
        message.warning('현재 URL과 동일합니다.');
        return;
      }

      loading.value = true;

      try {
        // URL 인코딩은 QrService에서 처리됨
        const success = await QrService.updateTargetUrl(props.qrData._id, newUrl.value.trim());

        if (success) {
          emit('success');
          emit('update:visible', false);
        }
      } finally {
        loading.value = false;
      }
    };

    const restoreUrl = async (historyRecord) => {
      loading.value = true;

      try {
        const success = await QrService.restoreUrl(props.qrData._id, historyRecord._id);

        if (success) {
          emit('success');
          emit('update:visible', false);
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      loading,
      newUrl,
      isValidUrl,
      hasKorean,
      urlHistory,
      historyColumns,
      validateUrl,
      handleCancel,
      handleSubmit,
      restoreUrl
    };
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.modal-content {
  padding: $spacing-md 0;
}

.qr-info-section {
  background-color: $bg-light;
  padding: $spacing-md;
  border-radius: $border-radius-md;

  .info-row {
    display: flex;
    align-items: center;
    margin-bottom: $spacing-sm;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      width: 100px;
      color: $text-muted;
      font-size: $font-size-sm;
    }

    .value {
      font-weight: $font-weight-medium;
    }

    code {
      background-color: $bg-white;
      padding: 2px 6px;
      border-radius: $border-radius-sm;
      font-size: $font-size-sm;
    }
  }
}

.input-hint {
  font-size: $font-size-xs;
  margin-top: 4px;

  &.error {
    color: $danger-color;
  }

  &.info {
    color: #1890ff;

    i {
      margin-right: 4px;
    }
  }
}

.history-section {
  margin-top: $spacing-md;

  h4 {
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    margin-bottom: $spacing-sm;
    color: $text-muted;
  }

  .url-cell {
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: $font-size-sm;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: $spacing-sm;
  padding-top: $spacing-md;
  border-top: 1px solid $border-color;
  margin-top: $spacing-md;
}

.yellow-button {
  background-color: $yellow-button-color;
  border-color: $yellow-button-color;
  color: $text-dark;

  &:hover {
    background-color: $yellow-button-color-hover;
    border-color: $yellow-button-color-hover;
  }
}
</style>
